import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useConfig } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gear, Envelope, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import DriveStatusCard from "@/components/DriveStatusCard";

export default function SettingsPage() {
  const cfg = useConfig();
  const [settings, setSettings] = useState(null);
  const [emailsText, setEmailsText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setEmailsText((data.manager_emails || []).join(", "));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const emails = emailsText.split(",").map(s=>s.trim()).filter(Boolean);
      const { data } = await api.put("/settings", { manager_emails: emails });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="px-8 py-8 text-ink-soft">Loading…</div>;

  return (
    <div className="px-4 md:px-8 py-8 max-w-3xl mx-auto w-full">
      <div className="flex items-center gap-3 mb-6">
        <Gear size={22} weight="duotone" />
        <h1 className="font-heading text-4xl">Settings</h1>
      </div>

      <DriveStatusCard />

      <section className="p-5 rounded-2xl border border-line bg-surface/40 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Envelope size={18} weight="duotone" />
          <h2 className="font-heading text-2xl">Manager notification emails</h2>
        </div>
        <p className="text-sm text-ink-soft mb-3">
          When Tracey can't answer a question, these managers get notified. Separate multiple emails with commas.
        </p>
        <Label>Manager emails</Label>
        <Input data-testid="mgr-emails" value={emailsText} onChange={(e)=>setEmailsText(e.target.value)}
          placeholder="sallie@gkpropertieslv.com, tracey@gkpropertieslv.com" className="mt-1 mb-3" />

        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
          cfg.email_delivery_enabled
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : "bg-amber-50 text-amber-900 border-amber-200"
        }`}>
          {cfg.email_delivery_enabled
            ? <><CheckCircle size={13} weight="fill" /> Email delivery is live (Resend)</>
            : <><WarningCircle size={13} weight="fill" /> Email delivery is mocked — alerts are stored in-app only</>}
        </div>
      </section>

      <section className="p-5 rounded-2xl border border-line bg-surface/40 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Gear size={18} weight="duotone" />
          <h2 className="font-heading text-2xl">Sign-in configuration</h2>
        </div>
        <ul className="text-sm text-ink-soft space-y-1">
          <li>Approved organization: <span className="font-mono text-gk-charcoal">@{cfg.approved_email_domain}</span></li>
          <li>Microsoft / Outlook SSO: <span className={cfg.microsoft_configured ? "text-emerald-800" : "text-amber-900"}>{cfg.microsoft_configured ? "Live" : "Not yet configured"}</span></li>
          <li>Public signup: <span className={cfg.self_signup_enabled ? "text-amber-900" : "text-emerald-800"}>{cfg.self_signup_enabled ? "Enabled (preview / dev)" : "Disabled — production ready"}</span></li>
          <li>Preview bypass: <span className={cfg.preview_mode ? "text-amber-900" : "text-emerald-800"}>{cfg.preview_mode ? "Enabled — TURN OFF before publishing" : "Disabled"}</span></li>
        </ul>
      </section>

      <Button onClick={save} disabled={busy} data-testid="save-settings"
        className="rounded-full bg-gk-red hover:bg-gk-red-bright text-white h-11 px-6">
        {busy ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
